import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Information } from "@/components/Information/Information";

import { Draft, DraftTiming } from "../utils/inboxDraft.types";
import { TimingDropdown } from "./TimingDropdown";

const NOW_TIMING: DraftTiming = {
	id: "now",
	label: "Now",
	shortLabel: "Now",
	value: "now",
	scheduledInDays: 0,
};

interface DraftTimingRowProps {
	activeDraft: Draft | undefined;
	inviteBaselineDays: number | null;
	activeIsInviteFollowUp: boolean;
	/** The draft waits for the sender's pending invitation to be accepted (PREC-4216). */
	waitsForAcceptance: boolean;
	portalContainer?: HTMLElement | null;
	onTimingChange: (timing: DraftTiming) => void;
	onToggleCcBcc: () => void;
}

/**
 * The editor's "Send:" row (extracted from `DraftEditorPanel`, which sits at the length limit): the
 * timing, counted from today, from a preceding invite (an invite follow-up), or from the
 * acceptance of the sender's pending invitation ("when they accept", PREC-4216); and the CC/BCC
 * toggle for an email draft.
 */
export const DraftTimingRow: FunctionComponent<DraftTimingRowProps> = ({
	activeDraft,
	inviteBaselineDays,
	activeIsInviteFollowUp,
	waitsForAcceptance,
	portalContainer,
	onTimingChange,
	onToggleCcBcc,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex items-center gap-2 px-3 py-2 border-t border-border-light">
			<div className="flex items-center gap-1">
				<span className="text-xs text-text-meta">Send:</span>
				<TimingDropdown
					afterAcceptance={waitsForAcceptance}
					dropdownPosition="top-left"
					portalContainer={portalContainer}
					relativeToInviteDays={
						activeIsInviteFollowUp && inviteBaselineDays !== null ? inviteBaselineDays : undefined
					}
					value={activeDraft?.timing ?? NOW_TIMING}
					onSelect={onTimingChange}
				/>
				{(activeIsInviteFollowUp || waitsForAcceptance) && (
					<Information
						iconClassName="w-3 h-3 text-text-meta"
						information={t("inbox.draft.acceptedConnectionTooltip")}
					/>
				)}
			</div>
			{activeDraft?.from?.type === "email" && (
				<button
					className="text-xs text-text-meta hover:text-brand-light cursor-pointer"
					type="button"
					onClick={onToggleCcBcc}
				>
					{activeDraft?.showCcBcc ? "Hide CC/BCC" : "CC / BCC"}
				</button>
			)}
		</div>
	);
};
